import "@blocknote/mantine/style.css";

import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";

import { useCallback, useEffect, useState } from "react";
import {
  useCreateBlockNote,
  // ImageCaptionButton,
} from "@blocknote/react";

import { BlockNoteView } from "@blocknote/mantine";
import "@/styles/textEditorStyles.css";
import { updateEditorContent } from "@/services/apiEditor";
import { useLoadEditorContent } from "@/features/proposal/editor/useLoadEditorContent";
import { useQueryClient } from "@tanstack/react-query";

import ResizableChat from "@/features/chat/ResizableChat";
import EditorToolbar from "./EditorToolbar";
import { useUiStore } from "@/stores/UiStore";
import { editorTheme } from "./editorTheme";
import DocumentSkeleton from "@/ui/DocumentSkeleton";
import { useProposalStore } from "@/stores/ProposalStore";
import { debounce } from "@/utils/helpers";

export default function Editor({ proposal_id, searchType, docIds }) {
  const {
    data: content,
    isLoading,
    isSuccess,
  } = useLoadEditorContent(proposal_id, searchType);
  const [isSaving, setIsSaving] = useState(false); // Add state for saving status
  const [lastSaved, setLastSaved] = useState(null); // Add state for last saved time
  const [selection, setSelection] = useState(null);
  const updateProposalBlocks = useProposalStore(
    (state) => state.updateProposalBlocks
  );
  const queryClient = useQueryClient();

  const debouncedSave = useCallback(
    debounce(async (content) => {
      if (!isLoading) {
        setIsSaving(true); // Set saving status to true
        updateProposalBlocks(searchType, proposal_id, content.blocks);
        await updateEditorContent(proposal_id, content, searchType);
        queryClient.invalidateQueries(["proposalsContracts"]);

        setLastSaved(
          new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        ); // Set last saved time
        setIsSaving(false); // Set saving status to false after saving
      }
    }, 300),
    [proposal_id, isLoading]
  );
  // Creates a new editor instance.
  const editor = useCreateBlockNote({
    // If the editor contents were previously saved, restores them.
  });
  // Serializes and saves the editor contents to local storage.
  async function onEditorContentChange(editor) {
    localStorage.setItem(
      `editor-content-${proposal_id}`,
      JSON.stringify(editor.document)
    );
    await debouncedSave({ blocks: editor.document });
  }

  function onEditorSelectionCapture() {
    // concatenate all the strings in the blocks
    setSelection(
      editor
        ?.getSelection()
        ?.blocks.map((block) => block.content.map((b) => b.text).join("\n"))
        .join("\n")
    );
  }

  useEffect(() => {
    useUiStore.setState({
      wideLayout: true,
    });
    if (editor) {
      // Check if editor is defined
      if (isSuccess && content?.blocks) {
        editor.replaceBlocks(editor.document, content?.blocks);
      }
    }
    // This function will be called when the component is unmounted
    return () =>
      useUiStore.setState({
        wideLayout: false,
      });
  }, [editor, isSuccess, content]);

  const convertMarkdowntoWord = async () => {
    try {
      const htmlText = await editor.blocksToFullHTML(editor.document);
      const opt = { lineNumber: true };
      asBlob(htmlText, opt).then((blob) => {
        const fileName = content?.[0]?.content?.[0]?.text || "proposal";
        saveAs(blob, fileName);
      });
    } catch (error) {
      console.error("Error in conversion:", error);
    }
  };

  // Renders the editor instance using a React component.
  return (
    <ResizableChat
      proposal_id={proposal_id}
      searchType={searchType || "upload"}
      purpose={"proposal"}
      context={selection}
      docIds={docIds}
      className={`shadow-md min-h-screen bg-[var(--gray-50)] border-gray-200 rounded-md`}
    >
      {isLoading && <DocumentSkeleton />}
      <BlockNoteView
        editor={editor}
        theme={editorTheme}
        onChange={onEditorContentChange}
        onSelectCapture={onEditorSelectionCapture}
        editable={!isLoading}
        formattingToolbar={false}
        className="pt-4"
      >
        <EditorToolbar
          isSaving={isSaving}
          isEditable={!isLoading}
          convertMarkdowntoWord={convertMarkdowntoWord}
          lastSaved={lastSaved}
        />
      </BlockNoteView>
    </ResizableChat>
  );
}
